/**
 * Check-in hub. Lists configured stations and provides the entry point to the
 * kiosk station view and the live roster.
 */
import { Badge, Button, Card, CardContent, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { MonitorSmartphone, ScanLine, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listStations, type StationDto } from '../../lib/checkins-api'

const kindVariant: Record<string, 'primary' | 'secondary' | 'gold' | 'neutral'> = {
  checkin: 'primary',
  self: 'secondary',
  roster: 'gold',
  print: 'neutral',
}

export function CheckinsHub() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['checkin-stations'],
    queryFn: listStations,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Check-in"
        description="Child-safety-grade attendance: stations, secure pickup, and labels."
        actions={
          <Button asChild>
            <Link to="/app/checkins/kiosk">
              <ScanLine /> Open station
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-start gap-3 p-5">
            <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-card text-secondary">
              <ScanLine className="size-5" />
            </div>
            <div>
              <div className="font-medium">Station</div>
              <p className="text-sm text-muted">Check people in and out at the kiosk.</p>
              <Link
                to="/app/checkins/kiosk"
                className="mt-1 inline-block text-sm text-secondary underline-offset-4 hover:underline"
              >
                Open →
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-start gap-3 p-5">
            <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-card text-secondary">
              <MonitorSmartphone className="size-5" />
            </div>
            <div>
              <div className="font-medium">Live roster</div>
              <p className="text-sm text-muted">See who is currently checked in per session.</p>
              <Link
                to="/app/checkins/roster"
                className="mt-1 inline-block text-sm text-secondary underline-offset-4 hover:underline"
              >
                Open →
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-start gap-3 p-5">
            <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-card text-secondary">
              <Tag className="size-5" />
            </div>
            <div>
              <div className="font-medium">Labels</div>
              <p className="text-sm text-muted">Name tags, child tags, and guardian pickup tags.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold tracking-tight">Stations</h2>
        {isLoading ? (
          <Card className="flex items-center justify-center gap-2 py-16 text-muted">
            <Spinner /> Loading stations…
          </Card>
        ) : isError ? (
          <Card className="space-y-2 py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load stations.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </Card>
        ) : !data || data.length === 0 ? (
          <Card className="py-16 text-center text-sm text-muted">
            No stations configured yet. Stations let you pair kiosk devices and presets.
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.map((s: StationDto) => (
              <Card key={s.id} className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-medium">{s.name}</div>
                  <Badge variant={kindVariant[s.kind] ?? 'neutral'} className="capitalize">
                    {s.kind}
                  </Badge>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-dim">
                  {s.isActive ? (
                    <span className="text-success">Active</span>
                  ) : (
                    <span className="text-muted">Inactive</span>
                  )}
                  <span>·</span>
                  <span>{s.isPaired ? 'Device paired' : 'Not paired'}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
